/**
 * SSE Event Types
 * Standardized event types for Server-Sent Events
 */

// Message status constants (mirrors backend)
export const MESSAGE_STATUS = {
  IN_PROGRESS: 'in_progress',
  PROCESSED: 'processed',
  FAILED: 'failed',
  CANCELED: 'canceled'
}

// Bot lifecycle events
export const SSE_EVENTS = {
  BOT_CREATED: 'bot-created',
  BOT_UPDATED: 'bot-updated',
  BOT_TITLE_UPDATED: 'bot-title-updated',
  BOT_DELETED: 'bot-deleted',
  BOT_ARCHIVED: 'bot-archived',
  BOT_RESTORED: 'bot-restored',
  BOT_CODE_GENERATED: 'bot-code-generated',
  
  // Bot message status events
  BOT_MESSAGE_STARTED: 'bot-message-started',
  BOT_MESSAGE_USER: 'bot-message-user',
  BOT_MESSAGE_CHUNK: 'bot-message-chunk',
  // The model's reasoning summary, streamed ahead of the answer when the user
  // ticked Thinking. Carries `accumulated` like BOT_MESSAGE_CHUNK does, and can
  // arrive before any content event for that msgId.
  BOT_MESSAGE_REASONING: 'bot-message-reasoning',
  // The code being written by generate_trading_code, decoded, for the live
  // snippet under the running card. Payload { botId, msgId: <tool row>, content }.
  // Deltas only — no `accumulated` — throttled by the server, never persisted or
  // replayed on reload, and nothing follows a cancel.
  BOT_MESSAGE_CODE_CHUNK: 'bot-message-code-chunk',
  // The model's reasoning summary while generate_trading_code thinks, ahead of
  // the code. Payload { botId, msgId: <tool row>, accumulated }: the WHOLE summary
  // so far (a replace, not an append — a tab reloading mid-thought catches up on
  // the next frame), throttled by the server, never persisted or replayed, and
  // nothing follows a cancel. Not BOT_MESSAGE_REASONING: that one is folded into
  // assistant messages and would conjure one up for a tool row.
  BOT_MESSAGE_CODE_REASONING: 'bot-message-code-reasoning',
  BOT_MESSAGE_DONE: 'bot-message-done',
  BOT_MESSAGE_DELETED: 'bot-message-deleted',
  BOT_MESSAGE_FAILED: 'bot-message-failed',
  BOT_MESSAGE_TYPE_CHANGED: 'bot-message-type-changed',
  BOT_MESSAGE_TOOL_STARTED: 'bot-message-tool-started',
  BOT_MESSAGE_TOOL_DONE: 'bot-message-tool-done',
  BOT_MESSAGE_CANCELED: 'bot-message-canceled',

  // Bot credits exhausted (no remaining subscription/addon credits)
  BOT_CREDITS_EXHAUSTED: 'bot-credits-exhausted',

  // A run was refused because the plan does not include it (not a usage limit).
  // Payload carries { feature: 'backtest' | 'optimizer' }.
  BOT_UPGRADE_REQUIRED: 'bot-upgrade-required',

  // Bot backtest events
  BOT_BACKTEST_STATUS: 'bot-backtest-status',
  BOT_BACKTEST_COMPLETED: 'bot-backtest-completed',
  
  // Bot optimizer events
  BOT_OPTIMIZER_STATUS: 'bot-optimizer-status',
  BOT_OPTIMIZER_COMPLETED: 'bot-optimizer-completed',

  // Walkthrough verification (the chat's Auto-verify loop). STATUS carries each stage
  // of a run_walkthrough tool call; READY fires once when the turn has ended and the run
  // is there to watch — the cue to open walkthrough mode. Its `verified` flag says whether
  // the app's verdict passed (true) or the user simply asked to see the run (false).
  BOT_WALKTHROUGH_STATUS: 'bot-walkthrough-status',
  BOT_WALKTHROUGH_READY: 'bot-walkthrough-ready',
  
  // Strategy lifecycle events
  STRATEGY_ARCHIVED: 'strategy-archived',
  STRATEGY_DELETED: 'strategy-deleted',
  STRATEGY_RESTORED: 'strategy-restored',
  
  // Strategy message events (for reference)
  STRATEGY_MESSAGE_USER: 'strategy-message-user',
  STRATEGY_MESSAGE_AGENT: 'strategy-message-agent',
  STRATEGY_MESSAGE_LIST: 'strategy-message-list',
  STRATEGY_OUTLINE: 'strategy-outline',
  STRATEGY_CODE: 'strategy-code',
  STRATEGY_CODE_STATE: 'strategy-code-state',
  STRATEGY_BACKTEST_PROCESSING: 'strategy-backtest-processing',
}

/**
 * Check if an event is a bot lifecycle event
 */
export function isBotLifecycleEvent(event) {
  return [
    SSE_EVENTS.BOT_CREATED,
    SSE_EVENTS.BOT_UPDATED,
    SSE_EVENTS.BOT_TITLE_UPDATED,
    SSE_EVENTS.BOT_DELETED,
    SSE_EVENTS.BOT_ARCHIVED,
    SSE_EVENTS.BOT_RESTORED,
    SSE_EVENTS.BOT_CODE_GENERATED,
  ].includes(event)
}

/**
 * Check if an event is a bot message event
 */
export function isBotMessageEvent(event) {
  return [
    SSE_EVENTS.BOT_MESSAGE_STARTED,
    SSE_EVENTS.BOT_MESSAGE_USER,
    SSE_EVENTS.BOT_MESSAGE_CHUNK,
    SSE_EVENTS.BOT_MESSAGE_CODE_CHUNK,
    SSE_EVENTS.BOT_MESSAGE_CODE_REASONING,
    SSE_EVENTS.BOT_MESSAGE_DONE,
    SSE_EVENTS.BOT_MESSAGE_DELETED,
    SSE_EVENTS.BOT_MESSAGE_FAILED,
    SSE_EVENTS.BOT_MESSAGE_TYPE_CHANGED,
    SSE_EVENTS.BOT_MESSAGE_TOOL_STARTED,
    SSE_EVENTS.BOT_MESSAGE_TOOL_DONE,
    SSE_EVENTS.BOT_MESSAGE_CANCELED,
  ].includes(event)
}

/**
 * Check if an event is a strategy lifecycle event
 */
export function isStrategyLifecycleEvent(event) {
  return [
    SSE_EVENTS.STRATEGY_ARCHIVED,
    SSE_EVENTS.STRATEGY_DELETED,
    SSE_EVENTS.STRATEGY_RESTORED,
  ].includes(event)
}
